// src/redux/services/streamApi.js
// ✅ HLS STREAM API (VPS) - RTK Query (Normalized baseUrl + Bearer + x-auth-token)

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const normalizeBaseUrl = () => {
  // Allow env to be either:
  // 1) https://app.krush.in        (no /api)
  // 2) https://app.krush.in/api    (with /api)
  // 3) http://localhost:7600       (no /api)
  // 4) http://localhost:7600/api   (with /api)
  const raw = process.env.REACT_APP_BACKEND_URL || "http://localhost:7600";

  const trimmed = raw.replace(/\/$/, "");
  return trimmed.endsWith("/api") ? trimmed : `${trimmed}/api`;
};

const baseQuery = fetchBaseQuery({
  baseUrl: normalizeBaseUrl(),
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth?.token || localStorage.getItem("token");

    if (token) {
      // Support both styles (some endpoints might use either)
      headers.set("x-auth-token", token);
      headers.set("Authorization", `Bearer ${token}`);
    }

    return headers;
  },
});

export const streamApi = createApi({
  reducerPath: "streamApi",
  baseQuery,
  tagTypes: ["Streams", "MyStreams", "StreamStats"],
  endpoints: (builder) => ({
    // ✅ START HLS STREAM (VPS)
    startStream: builder.mutation({
      query: ({ cameraId, streamType = "live" } = {}) => ({
        url: "/streams/start",
        method: "POST",
        body: { cameraId, streamType },
      }),
      invalidatesTags: ["MyStreams", "StreamStats"],
    }),

    // ✅ STOP HLS STREAM (VPS)
    stopStream: builder.mutation({
      query: ({ sessionId } = {}) => ({
        url: "/streams/stop",
        method: "POST",
        body: { sessionId },
      }),
      invalidatesTags: ["MyStreams", "StreamStats"],
    }),

    // ✅ STREAM HEARTBEAT
    sendHeartbeat: builder.mutation({
      query: ({ sessionId } = {}) => ({
        url: "/streams/heartbeat",
        method: "POST",
        body: { sessionId },
      }),
    }),

    // ===== GET MY ACTIVE STREAMS =====
    // NOTE: Returns ARRAY directly (already unwrapped)
    getMyStreams: builder.query({
      query: () => "/streams/my",
      providesTags: ["MyStreams"],
      transformResponse: (response) => response?.data || response || [],
    }),

    // ===== STOP ALL MY STREAMS =====
    stopAllMyStreams: builder.mutation({
      query: () => ({
        url: "/streams/stop-all",
        method: "POST",
      }),
      invalidatesTags: ["MyStreams", "Streams", "StreamStats"],
    }),

    // ===== GET DEVICE STREAMS =====
    // NOTE: Returns ARRAY directly (already unwrapped)
    getDeviceStreams: builder.query({
      query: (deviceId) => `/streams/device/${deviceId}`,
      providesTags: (result, error, deviceId) => [{ type: "Streams", id: deviceId }],
      transformResponse: (response) => response?.data || response || [],
    }),

    // ===== GET STREAM STATISTICS (Admin) =====
    // NOTE: Returns OBJECT directly (already unwrapped)
    getStreamStats: builder.query({
      query: () => "/streams/stats",
      providesTags: ["StreamStats"],
      transformResponse: (response) => response?.data || response || {},
    }),

    // ===== FORCE STOP CAMERA STREAMS (Admin) =====
    forceStopCameraStreams: builder.mutation({
      query: (cameraId) => ({
        url: `/streams/camera/${cameraId}/force-stop`,
        method: "POST",
      }),
      invalidatesTags: ["Streams", "MyStreams", "StreamStats"],
    }),

    // ===== CLEANUP STALE STREAMS (Admin) =====
    cleanupStaleStreams: builder.mutation({
      query: () => ({
        url: "/streams/cleanup",
        method: "POST",
      }),
      invalidatesTags: ["Streams", "MyStreams", "StreamStats"],
    }),
  }),
});

export const {
  useStartStreamMutation,
  useStopStreamMutation,
  useSendHeartbeatMutation,
  useGetMyStreamsQuery,
  useStopAllMyStreamsMutation,
  useGetDeviceStreamsQuery,
  useGetStreamStatsQuery,
  useForceStopCameraStreamsMutation,
  useCleanupStaleStreamsMutation,
} = streamApi;
